import React, { useState } from 'react';
import styled from '@emotion/styled';
import { supabase } from '../lib/supabase';

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.8);
  z-index: 2000;
`;

const Container = styled.div`
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  padding: 32px;
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

const Title = styled.h2`
  font-family: var(--font-heading);
  font-size: 24px;
  color: var(--color-text);
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  margin: 0 0 24px 0;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  font-size: 16px;
  font-family: var(--font-body);
  background: var(--color-background);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  outline: none;
  margin-bottom: 8px;

  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px var(--color-primary-light, rgba(99, 102, 241, 0.1));
  }

  &::placeholder {
    color: var(--color-text-secondary);
  }
`;

const HelperText = styled.p`
  color: var(--color-text-secondary);
  font-size: 12px;
  text-align: left;
  margin: 0 0 16px 0;
`;

const ErrorText = styled.p`
  color: var(--color-error);
  font-size: 12px;
  text-align: left;
  margin: 8px 0;
`;

const Button = styled.button`
  width: 100%;
  padding: 12px 24px;
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-family: var(--font-body);
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--color-primary-shadow, rgba(99, 102, 241, 0.3));
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface UsernameSetupProps {
  onComplete: () => void;
}

export const UsernameSetup: React.FC<UsernameSetupProps> = ({ onComplete }) => {
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateUsername = (value: string): boolean => {
    if (value.length < 3) {
      setError('Username must be at least 3 characters');
      return false;
    }
    if (value.length > 20) {
      setError('Username must be 20 characters or less');
      return false;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
      setError('Username can only contain letters, numbers, - and _');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateUsername(username)) {
      return;
    }

    setLoading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Check if username is already taken
      const { data: existingUser } = await supabase
        .from('user_data')
        .select('username')
        .eq('username', username)
        .maybeSingle();

      if (existingUser) {
        setError('Username is already taken');
        setLoading(false);
        return;
      }

      // Save username to user_data
      const { error: upsertError } = await supabase
        .from('user_data')
        .upsert({
          user_id: user.id,
          username,
          display_name: username,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (upsertError) {
        throw upsertError;
      }

      // Update user metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: { username }
      });

      if (updateError) {
        console.error('Error updating user metadata:', updateError);
      }

      onComplete();
    } catch (err: any) {
      setError(err.message || 'Failed to set username');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal>
      <Container>
        <Title>Choose Your Username</Title>
        <Subtitle>This is how you'll appear on the leaderboard</Subtitle>
        
        <form onSubmit={handleSubmit}>
          <Input
            type="text"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              validateUsername(e.target.value);
            }}
            placeholder="Enter username"
            autoFocus
            disabled={loading}
          />
          <HelperText>
            3-20 characters, letters, numbers, - and _ only
          </HelperText>
          
          {error && <ErrorText>{error}</ErrorText>}
          
          <Button 
            type="submit" 
            disabled={!username || !!error || loading}
          >
            {loading ? 'Setting username...' : 'Continue'}
          </Button>
        </form>
      </Container>
    </Modal>
  );
};